import { Radio } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import type { ReactNode } from "react";
import type { NewRequisitionForm } from "../../recoil/types";
import {
  CarrierExpandedHelperBox,
  CarrierExpandedPanelGroup,
  CarrierPanelStack,
  HelperCopy,
  NewReqCheckboxLabelRow,
  NewReqHelperTextBox,
  NewReqHelperTextLines,
  NewReqTestOptionLabelColumn,
  NewReqTestOptionRow,
  OptionGenes,
  OptionLabel,
  SoloCheckboxWrapper,
  StyledFormLabelClinical,
} from "./NewRequisition.styles";
import {
  carrierPanelRowPtSx,
  carrierPanelRowPySx,
  testPanelRadioSx,
  fullWidthSx,
  getCarrierPanelLabelSx,
  getDisabledOpacitySx,
  NESTED_OPTION_INDENT,
} from "./testPanelStyles";
import { EXPANDED_CARRIER_PANEL, type ExpandedCarrierPanel } from "./types";

export type CarrierPanelSelection = "standard" | ExpandedCarrierPanel;

interface CarrierScreenPanelSelectProps {
  disabled: boolean;
  carrierScreen: boolean;
  expandedCarrierPanel: ExpandedCarrierPanel | null;
  onSelect: (selection: CarrierPanelSelection | null) => void;
  visibleExpandedCarrierPanels: ExpandedCarrierPanel[];
  afterFivePanelContent?: ReactNode;
  after130PanelContent?: ReactNode;
}

const FIVE_GENE_HELPER_LINES = [
  "Fetal risk assessment will be provided for all positive carrier test results, with O28.5 applied automatically.",
];

const EXPANDED_CARRIER_PANEL_HELPER_LINES = [
  "Males are not screened for X-linked genes, counts listed are for females.",
  "Fetal risk for X-linked genes and CYP21A2 are provided via fetal sex.",
];

const renderHelperLines = (lines: string[]) => (
  <NewReqHelperTextLines>
    {lines.map((line) => (
      <HelperCopy key={line}>{line}</HelperCopy>
    ))}
  </NewReqHelperTextLines>
);

type ExpandedCarrierPanelOption = {
  id: string;
  ariaLabel: string;
  rowSx: typeof carrierPanelRowPySx | typeof carrierPanelRowPtSx;
  label: ReactNode;
};

const EXPANDED_CARRIER_PANEL_OPTIONS: Record<
  ExpandedCarrierPanel,
  ExpandedCarrierPanelOption
> = {
  [EXPANDED_CARRIER_PANEL.CARRIER_113_CUSTOM]: {
    id: "carrier-panel-113-custom",
    ariaLabel: "113-gene panel Carrier only",
    rowSx: carrierPanelRowPtSx,
    label: (
      <>
        <strong>113-gene panel </strong>
        <em>Carrier only</em>
      </>
    ),
  },
  [EXPANDED_CARRIER_PANEL.CARRIER_130]: {
    id: "carrier-panel-130-gene",
    ariaLabel: "130-gene panel",
    rowSx: carrierPanelRowPySx,
    label: <strong>130-gene panel</strong>,
  },
  [EXPANDED_CARRIER_PANEL.CARRIER_611]: {
    id: "carrier-panel-611-gene",
    ariaLabel: "611-gene panel Carrier Only",
    rowSx: carrierPanelRowPtSx,
    label: (
      <>
        <strong>611-gene panel </strong>
        <em>Carrier Only</em>
      </>
    ),
  },
};

export const getCarrierPanelSelectionPatch = (
  selection: CarrierPanelSelection | null,
): Partial<NewRequisitionForm> => {
  if (selection === null) {
    return {
      carrierScreen: false,
      expandedCarrierPanel: null,
      isFragileX: false,
      isAcogPlus: false,
      cfDNAOptOut: false,
      O285_carrier: false,
    };
  }

  if (selection === "standard") {
    return {
      carrierScreen: true,
      expandedCarrierPanel: null,
      O285_carrier: true,
    };
  }

  return {
    carrierScreen: true,
    expandedCarrierPanel: selection,
    isFragileX: false,
    isAcogPlus: false,
    cfDNAOptOut: false,
    O285_carrier: true,
  };
};

const CarrierScreenPanelSelect = ({
  disabled,
  carrierScreen,
  expandedCarrierPanel,
  onSelect,
  visibleExpandedCarrierPanels,
  afterFivePanelContent,
  after130PanelContent,
}: CarrierScreenPanelSelectProps) => {
  const currentSelection: CarrierPanelSelection | null = !carrierScreen
    ? null
    : (expandedCarrierPanel ?? "standard");

  const panelLabelSx = getCarrierPanelLabelSx(disabled);
  const helperOpacityStyle = getDisabledOpacitySx(disabled);

  const selectPanel = (selection: CarrierPanelSelection) => {
    if (disabled) {
      return;
    }
    onSelect(currentSelection === selection ? null : selection);
  };

  const selectStandard = () => selectPanel("standard");

  return (
    <CarrierPanelStack>
      <NewReqTestOptionRow>
        <NewReqTestOptionLabelColumn>
          <NewReqCheckboxLabelRow sx={fullWidthSx}>
            <SoloCheckboxWrapper>
              <Radio
                id="carrier-panel-5-gene"
                sx={testPanelRadioSx}
                checked={currentSelection === "standard"}
                disabled={disabled}
                name="carrierPanel"
                value="standard"
                onChange={() => onSelect("standard")}
                onClick={
                  currentSelection === "standard" ? selectStandard : undefined
                }
                inputProps={{ "aria-label": "5-gene panel" }}
              />
            </SoloCheckboxWrapper>
            <StyledFormLabelClinical sx={panelLabelSx} onClick={selectStandard}>
              <OptionLabel>
                <strong>5-gene panel</strong>
              </OptionLabel>
              <OptionGenes>CFTR • SMN1 • HBB • HBA1 • HBA2</OptionGenes>
            </StyledFormLabelClinical>
          </NewReqCheckboxLabelRow>
        </NewReqTestOptionLabelColumn>
        <NewReqHelperTextBox style={helperOpacityStyle}>
          <InfoOutlined />
          {renderHelperLines(FIVE_GENE_HELPER_LINES)}
        </NewReqHelperTextBox>
      </NewReqTestOptionRow>

      {afterFivePanelContent}

      {visibleExpandedCarrierPanels.length > 0 && (
        <CarrierExpandedPanelGroup>
          <CarrierPanelStack>
            {visibleExpandedCarrierPanels.map((visibleExpandedPanel) => {
              const panelOption =
                EXPANDED_CARRIER_PANEL_OPTIONS[visibleExpandedPanel];
              const selectExpandedCarrierPanel = () =>
                selectPanel(visibleExpandedPanel);

              return (
                <CarrierPanelStack key={visibleExpandedPanel}>
                  <NewReqCheckboxLabelRow sx={panelOption.rowSx}>
                    <SoloCheckboxWrapper>
                      <Radio
                        id={panelOption.id}
                        sx={testPanelRadioSx}
                        checked={currentSelection === visibleExpandedPanel}
                        disabled={disabled}
                        name="carrierPanel"
                        value={visibleExpandedPanel}
                        onChange={() => onSelect(visibleExpandedPanel)}
                        onClick={
                          currentSelection === visibleExpandedPanel
                            ? selectExpandedCarrierPanel
                            : undefined
                        }
                        inputProps={{ "aria-label": panelOption.ariaLabel }}
                      />
                    </SoloCheckboxWrapper>
                    <StyledFormLabelClinical
                      sx={panelLabelSx}
                      onClick={selectExpandedCarrierPanel}
                    >
                      <OptionLabel>{panelOption.label}</OptionLabel>
                    </StyledFormLabelClinical>
                  </NewReqCheckboxLabelRow>

                  {visibleExpandedPanel ===
                    EXPANDED_CARRIER_PANEL.CARRIER_130 && after130PanelContent}
                </CarrierPanelStack>
              );
            })}
          </CarrierPanelStack>

          <CarrierExpandedHelperBox
            $disabled={disabled}
            sx={{ paddingInlineStart: NESTED_OPTION_INDENT }}
          >
            {renderHelperLines(EXPANDED_CARRIER_PANEL_HELPER_LINES)}
          </CarrierExpandedHelperBox>
        </CarrierExpandedPanelGroup>
      )}
    </CarrierPanelStack>
  );
};

export default CarrierScreenPanelSelect;
